/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconEdit } from "@shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconExpand } from "@shared/assets/Icons/IconExpand.svg";
import { getReflectorLicenses } from "../../Reflector/request/request";
import { useAuth0 } from "@auth0/auth0-react";
import EditLicenseModal from "./EditLicenseModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import DevicesModal from "../DevicesModal/DevicesModal";
import AddLicenseModal from "../LicenseModal/AddLicenseModal";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { ReflectorManagementOnboardingEmptyState } from "@shared/components/EmptyItems";
import { trimTrailingSlash } from "@shared/helpers/pathname";
import {
  getTableBodyMaxHeight,
  getInitialRowsPerPage,
  REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS,
} from "../../../helpers/tableViewport";

interface License {
	isExpired: boolean;
	usagePercentage: number;
	id: number;
	serial: string;
	description: string;
	activated: number;
	total: number;
	available: number;
	startDate: string;
	endDate: string;
	tenantId: number;
	type: string;
	status: number;
	createdAt: string;
	updatedAt: string;
	// Add devices field for super admin view
	devices?: string[];
	ReflectorInstances?: any[];
}

/**
 * Vertical chrome that is *not* table body on this screen, in px. Without an explicit
 * budget `GenericTable` falls back to a fixed 400px body, which strands most of a tall
 * viewport (CTX-244), so we follow the Reflector V2 convention (`ReflectorsPage`,
 * `ClusterPage`, `CloudElementsPage`) and hand it a `calc(100vh - Npx)` budget instead.
 *
 * Two of the three parts are conditional, and both hang off the route: the admin-center
 * route renders the "Add License" row but gets no tab strip (see `getRenderTabsByRoute`),
 * while the tenant route is the other way round.
 */
const PAGE_CHROME_PX = 150; // SideNav title row + page inset + card/table borders + pagination footer
const TAB_STRIP_PX = 52; // "Paid Licenses / PAYG" strip, tenant route only
const ADD_LICENSE_ROW_PX = 66; // admin-only header row above the table

const getPaidLicensesTableBodyMaxHeight = (
  hasTabStrip: boolean,
  hasAddLicenseRow: boolean
): string =>
  getTableBodyMaxHeight(
    PAGE_CHROME_PX +
      (hasTabStrip ? TAB_STRIP_PX : 0) +
      (hasAddLicenseRow ? ADD_LICENSE_ROW_PX : 0)
  );

const PaidLicenses: React.FC = () => {
  const location = useLocation();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [isDevicesModalOpen, setIsDevicesModalOpen] = useState(false);
  const [selectedInstances, setSelectedInstances] = useState<any[]>([]);
  const [isAddLicenseModalOpen, setIsAddLicenseModalOpen] = useState(false);
  const [prefillLicense, setPrefillLicense] = useState<License | null>(null);

  // Get user profile from Redux to check permissions
  const userProfile = useAppSelector((state) => state.userProfile);
  const tenantType = userProfile.TenantType;
  const { tenantId } = useAppSelector((state) => state.selection);

  // Check if user is super admin and in admin center context
  const isSuperAdmin = tenantType === "GLOBAL_ADMIN";
  const isAdminCenter =
    trimTrailingSlash(location.pathname) ===
    "/reflector-v2/admin-center/management/licensing";
  const shouldShowAdminFeatures = isSuperAdmin && isAdminCenter;

  const fetchLicenses = async () => {
    if (!tenantId) {
      setLicenses([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await getReflectorLicenses(token, tenantId);
      
      // Add mock devices data for super admin view (replace with actual API call)
      const licensesWithDevices = response.data.data.map((license: License) => ({
        ...license,
        devices: shouldShowAdminFeatures ? [`Device-${license.id}-1`, `Device-${license.id}-2`] : undefined
      }));
      
      setLicenses(licensesWithDevices);
    } catch (err) {
      setError("Failed to fetch licenses. Please try again.");
      console.error("Error fetching licenses:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [getAccessTokenSilently, shouldShowAdminFeatures, tenantId]);

  const handleEdit = (license: License) => {
    if (shouldShowAdminFeatures) {
      setPrefillLicense(license);
      setIsAddLicenseModalOpen(true);
    } else {
      setSelectedLicense(license);
      setIsEditModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedLicense(null);
  };

  const handleLicenseUpdated = () => {
    fetchLicenses();
  };

  const handleAddLicense = () => {
    setIsAddLicenseModalOpen(true);
  };

  const handleCreateLicense = (data: any) => {
    setIsAddLicenseModalOpen(false);
    setPrefillLicense(null);
    // Refetch licenses to display the newly created license
    fetchLicenses();
  };

  const handleDeviceAction = (licenseId: string, action: string) => {
    if (action === 'devices') {
      const license = licenses.find(l => String(l.id) === licenseId);
      setSelectedInstances(license?.ReflectorInstances || []);
      setIsDevicesModalOpen(true);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-');
  };

  // Build columns array conditionally
  const baseColumns: GenericTableColumn<License>[] = [
    { header: 'License', accessor: 'serial' },
    { header: 'Type', accessor: 'type' },
    { header: 'Description', accessor: 'description' },
    { header: 'Start date', accessor: 'startDate', renderCell: (row) => formatDate(row.startDate) },
    { header: 'End date', accessor: 'endDate', renderCell: (row) => formatDate(row.endDate) },
    { header: 'Activations', accessor: 'id', renderCell: (row) => `${row.activated} of ${row.total}` },
    { 
      header: 'Status', 
      accessor: 'status',
      renderCell: (row) => (
        <GenericTag label={!row.isExpired ? 'Active' : 'Expired'} variant={!row.isExpired ? 'success' : 'error'} />
      ) 
    },
    {
      header: "Actions",
      accessor: 'id',
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <GenericButton
            variant="secondary"
            size="sm"
            icon={<IconEdit className="w-8 h-8 text-semantic-text-secondary" />}
            isIconOnly
            onClick={() => handleEdit(row)}
            title="Edit license"
          />
          {shouldShowAdminFeatures && (
            <GenericButton
              variant="secondary"
              size="sm"
              icon={<IconExpand className="w-4 h-4 text-semantic-text-secondary" fill="currentColor" />}
              isIconOnly
              onClick={() => handleDeviceAction(String(row.id), 'devices')}
              title="Expand devices"
            />
          )}
        </div>
      ),
    },
  ];

  // Remove the conditional logic for admin features/devices column
  const columns: GenericTableColumn<License>[] = baseColumns;

  return (
    <>
      <div className="relative">
        {shouldShowAdminFeatures && (
          <div className="flex justify-end p-4 border-b border-gray-100">
            <GenericButton
              label="Add License"
              variant="brand"
              size="md"
              onClick={handleAddLicense}
            />
          </div>
        )}
        <DataStateView
          loading={isLoading}
          error={error}
          isEmpty={!isLoading && !error && licenses.length === 0}
          onRetry={fetchLicenses}
          loadingContent={<TableSkeleton columns={columns.length} rows={8} />}
          errorTitle="Failed to load licenses"
          emptyContent={
            <ReflectorManagementOnboardingEmptyState heading="You have no licenses yet" />
          }
        >
          <GenericTable
            columns={columns}
            data={licenses}
            initialRowsPerPage={getInitialRowsPerPage(licenses.length)}
            rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
            maxTableBodyHeight={getPaidLicensesTableBodyMaxHeight(
              !isAdminCenter,
              shouldShowAdminFeatures
            )}
          />
        </DataStateView>
      </div>
      <EditLicenseModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        license={selectedLicense}
        onLicenseUpdated={handleLicenseUpdated}
      />
      <DevicesModal
        isOpen={isDevicesModalOpen}
        onClose={() => setIsDevicesModalOpen(false)}
        instances={selectedInstances}
      />
      <AddLicenseModal
        isOpen={isAddLicenseModalOpen}
        onClose={() => {
          setIsAddLicenseModalOpen(false);
          setPrefillLicense(null);
        }}
        onCreate={handleCreateLicense}
        licenseTypes={[
          { value: "CTXR-1Y-L", label: "CTXR-1Y-L" },
          { value: "CTXR-2Y-L", label: "CTXR-2Y-L" },
        ]}
        prefill={prefillLicense}
      />
    </>
  );
};

export default PaidLicenses;
